import { useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import { fetchDashboard } from '../api/client';
import { fetchOverview } from '../api/helpCenter';
import { useHelpCenterAccessToken } from '../auth/useHelpCenterAccessToken';
import MetricCard from '../components/MetricCard';
import { formatNumber, formatDate, formatSequenceName } from '../utils/formatters';

export default function Dashboard() {
  const { getAccessTokenSilently } = useAuth0();
  const getHelpCenterAccessToken = useHelpCenterAccessToken();
  const [data, setData] = useState(null);
  const [hcData, setHcData] = useState(null);
  const [error, setError] = useState(null);
  const [hcError, setHcError] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();

      // Fetch both APIs in parallel — Mercury data shows even if Help Center is down
      const [mercuryResult, hcResult] = await Promise.allSettled([
        fetchDashboard(token),
        getHelpCenterAccessToken().then((helpCenterToken) => fetchOverview(helpCenterToken)),
      ]);

      if (mercuryResult.status === 'fulfilled') {
        setData(mercuryResult.value);
        setError(null);
      } else {
        setError(mercuryResult.reason?.message || 'Failed to load Mercury data');
      }

      if (hcResult.status === 'fulfilled') {
        setHcData(hcResult.value);
        setHcError(null);
      } else {
        setHcError(hcResult.reason?.message || 'Help Center unavailable');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, getHelpCenterAccessToken]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-text-secondary">Loading dashboard...</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="bg-alert/10 border-2 border-alert rounded-xl p-6 shadow-brutal">
        <p className="text-alert font-semibold">Failed to load dashboard</p>
        <p className="text-text-secondary text-sm mt-1">{error}</p>
        <button
          onClick={loadData}
          className="mt-3 text-sm text-primary font-medium hover:underline"
        >
          Retry
        </button>
      </div>
    );
  }

  const { summary, sequences, activity, health } = data || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-extrabold text-text-primary">Dashboard</h2>
        <div className="flex items-center gap-2">
          <span
            className={`inline-block w-2.5 h-2.5 rounded-full ${
              health?.status === 'healthy' ? 'bg-success' : 'bg-alert'
            }`}
          />
          <span className="text-sm text-text-secondary font-medium">
            {health?.status === 'healthy' ? 'System healthy' : 'Issues detected'}
          </span>
        </div>
      </div>

      {/* Email Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Total Contacts"
          value={formatNumber(summary?.totalContacts || 0)}
          variant="default"
        />
        <MetricCard
          label="Active Enrollments"
          value={formatNumber(summary?.activeEnrollments || 0)}
          variant="primary"
        />
        <MetricCard
          label="Sends Today"
          value={formatNumber(summary?.sendsToday || 0)}
          variant="success"
        />
        <MetricCard
          label="System Status"
          value={health?.status === 'healthy' ? 'OK' : 'Alert'}
          sublabel={health?.environment}
          variant={health?.status === 'healthy' ? 'success' : 'alert'}
        />
      </div>

      {/* Help Center Stats */}
      {hcError ? (
        <div className="bg-ink/5 border-2 border-ink/20 rounded-xl p-4 shadow-brutal-sm">
          <p className="text-sm text-text-secondary">Help Center data unavailable: {hcError}</p>
        </div>
      ) : hcData ? (
        <div>
          <h3 className="text-sm font-bold text-text-secondary uppercase tracking-wide mb-3">Help Center</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/help-center/articles">
              <MetricCard
                label="Published Articles"
                value={formatNumber(hcData.publishedArticles || 0)}
                variant="default"
              />
            </Link>
            <Link to="/help-center/tickets">
              <MetricCard
                label="Open Tickets"
                value={formatNumber(hcData.openTickets || 0)}
                variant={hcData.openTickets > 0 ? 'alert' : 'default'}
              />
            </Link>
            <Link to="/help-center/features">
              <MetricCard
                label="Pending Features"
                value={formatNumber(hcData.pendingFeatures || 0)}
                variant="primary"
              />
            </Link>
            <MetricCard
              label="Total Article Views"
              value={formatNumber(hcData.totalViews || 0)}
              variant="success"
            />
          </div>
        </div>
      ) : null}

      {/* Top Searches */}
      {hcData?.topSearches?.length > 0 && (
        <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal overflow-hidden">
          <div className="px-6 py-4 border-b-2 border-ink/10">
            <h3 className="text-lg font-bold text-text-primary">Top Help Center Searches</h3>
          </div>
          <div className="divide-y divide-ink/10">
            {hcData.topSearches.slice(0, 8).map((item, i) => (
              <div key={item.query} className="px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono text-text-secondary w-6">{i + 1}.</span>
                  <span className="text-sm font-medium text-text-primary">{item.query}</span>
                </div>
                <span className="text-sm text-text-secondary">{item.count} searches</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sequence Status Table */}
      <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal overflow-hidden">
        <div className="px-6 py-4 border-b-2 border-ink/10">
          <h3 className="text-lg font-bold text-text-primary">Sequence Status</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm font-semibold text-text-secondary">
                <th className="px-6 py-3">Sequence</th>
                <th className="px-6 py-3 text-right">Active</th>
                <th className="px-6 py-3 text-right">Completed</th>
                <th className="px-6 py-3 text-right">Cancelled</th>
                <th className="px-6 py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink/10">
              {(sequences || []).map((seq) => (
                <tr key={seq.sequenceId} className="hover:bg-background/50 transition-colors">
                  <td className="px-6 py-3 text-sm font-semibold text-text-primary">
                    {formatSequenceName(seq.sequenceId)}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-primary font-bold">
                    {seq.active}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-success font-medium">
                    {seq.completed}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-text-secondary">
                    {seq.cancelled}
                  </td>
                  <td className="px-6 py-3 text-sm text-right text-text-primary font-bold">
                    {seq.active + seq.completed + seq.cancelled}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-surface rounded-xl border-2 border-ink shadow-brutal overflow-hidden">
        <div className="px-6 py-4 border-b-2 border-ink/10">
          <h3 className="text-lg font-bold text-text-primary">Recent Activity</h3>
        </div>
        <div className="divide-y divide-ink/10">
          {(activity || []).length === 0 ? (
            <p className="px-6 py-8 text-text-secondary text-center text-sm">No recent activity</p>
          ) : (
            (activity || []).map((event, i) => (
              <div key={event.eventId || i} className="px-6 py-3 flex items-center justify-between">
                <div>
                  <span className="text-sm font-semibold text-text-primary">{event.eventType}</span>
                  <span className="ml-2 text-xs text-text-secondary">
                    {event.source} &middot; {event.contactId?.slice(0, 8)}...
                  </span>
                </div>
                <span className="text-xs text-text-secondary">{formatDate(event.timestamp)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <p className="text-xs text-text-secondary text-center">
        Last updated: {data?.fetchedAt ? formatDate(data.fetchedAt) : 'N/A'} &middot; Auto-refresh every 30s
      </p>
    </div>
  );
}
